import { $, el } from '../state/store.js';
import { currentUser, sendPasswordReset, signOut } from '../services/auth.js';
import {
  anonCount, cleanupLog, deleteUser, licenceHistory, listLicences, listUsers,
  notifyLicence, removeLicence, setLicence, setUserPassword, staleAnonCount
} from '../services/admin.js';
import { deleteTrial, listTrials, setTrialStatus } from '../services/trial.js';
import { deleteFeedback, listFeedback, markRead } from '../services/feedback.js';
import { calendarGrid, formatDate, formatDateTime, toYMD } from './calendar.js';
import { logoLockup } from './logo.js';
import { toast } from './toast.js';

/* ADMIN, BUILT FOR A PHONE.
 *
 * Not the desktop panel restyled — that was the first attempt and it was the
 * wrong shape however large the buttons got. A person row there carries five
 * actions side by side because it has 1040px to do it in; at 390px those wrap
 * into a block of near-identical targets, two of which are destructive, and
 * scanning a list becomes reading five buttons per entry.
 *
 * The shape a phone wants instead, and what each choice is for:
 *
 *   · A LIST OF CARDS, one line of identity and one of context. Scanning is
 *     the common act; acting on a specific person is the rarer one.
 *   · ACTIONS BEHIND A TAP, in a bottom sheet. It puts them within reach of a
 *     thumb, gives each one a full-width target that cannot be hit by
 *     accident, and — the real reason — lets a destructive action sit apart
 *     from the routine ones instead of beside them.
 *   · TABS AT THE BOTTOM, where the hand already is, carrying the counts that
 *     say whether anything needs attention.
 *   · SAFE-AREA INSETS, so the tab bar clears the home indicator and the
 *     header clears the notch.
 *
 * It shares every service with the desktop panel and duplicates no rule about
 * who may do what: the database answers that, the same way it does there.
 */

let root = null, active = 'users', counts = { trials: 0, feedback: 0 };
const views = {};

/* ── small shared pieces ─────────────────────────────────────────────────── */

const when = iso => {
  if (!iso) return 'never';
  const d = new Date(iso), diff = (Date.now() - d) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return Math.floor(diff / 60) + ' min ago';
  if (diff < 86400) return Math.floor(diff / 3600) + ' h ago';
  if (diff < 604800) return Math.floor(diff / 86400) + ' d ago';
  return formatDate(d);
};

const size = bytes => {
  const n = Number(bytes) || 0;
  if (!n) return '';
  if (n < 1048576) return Math.max(1, Math.round(n / 1024)) + ' KB';
  const mb = n / 1048576;
  return (mb < 10 ? mb.toFixed(1) : Math.round(mb)) + ' MB';
};

const endOfDay = ymd => ymd ? new Date(ymd + 'T23:59:59').toISOString() : null;
const plusDays = n => { const d = new Date(); d.setDate(d.getDate() + n); return toYMD(d); };

/* A BOTTOM SHEET.
   Dismissed by the backdrop or the handle, never by a stray tap inside it —
   somebody halfway through choosing an expiry should not lose the sheet
   because their thumb brushed the card. */
function sheet(title, build) {
  const back = el('div', 'msheet__back');
  const panel = el('div', 'msheet');
  panel.appendChild(el('div', 'msheet__grab'));
  const head = el('div', 'msheet__head');
  head.appendChild(el('h3', null, title));
  panel.appendChild(head);
  const body = el('div', 'msheet__body');
  panel.appendChild(body);
  const close = () => back.remove();
  back.onclick = e => { if (e.target === back) close(); };
  build(body, close);
  back.appendChild(panel);
  root.appendChild(back);
  /* Focus goes to the sheet, not left behind the backdrop, so a keyboard or
     screen reader follows the thing that just opened. */
  requestAnimationFrame(() => panel.scrollTop = 0);
  return close;
}

/* One action inside a sheet. `tone` marks the irreversible ones. */
function action(body, label, run, tone, note) {
  const b = el('button', 'mact' + (tone ? ' mact--' + tone : ''), label);
  if (note) {
    b.textContent = '';
    b.appendChild(el('span', 'mact__label', label));
    b.appendChild(el('span', 'mact__note', note));
  }
  b.onclick = async () => {
    b.disabled = true;
    try { await run(); }
    catch (e) { toast(e.message || 'That didn’t work.', true); b.disabled = false; }
  };
  body.appendChild(b);
  return b;
}

const confirmSheet = (title, detail, label, run) => sheet(title, (body, close) => {
  body.appendChild(el('p', 'msheet__note', detail));
  action(body, label, async () => { close(); await run(); }, 'danger');
  action(body, 'Cancel', () => close());
});

/* A date, chosen on a phone. Reuses the app's own calendarGrid so there is one
   calendar, and adds the note field the Approve flow needs. */
function pickWhen({ title, value, note = false }, done) {
  sheet(title, (body, close) => {
    let ymd = value;
    const read = el('p', 'msheet__note', 'Runs until ' + formatDate(new Date(ymd + 'T12:00:00')));
    body.appendChild(read);
    body.appendChild(calendarGrid(new Date(ymd + 'T12:00:00'), d => {
      ymd = toYMD(d);
      read.textContent = 'Runs until ' + formatDate(d);
    }));
    let msg = null;
    if (note) {
      const lab = el('label', 'mfield');
      lab.appendChild(el('span', null, 'Add a note (optional)'));
      msg = el('textarea', 'minput');
      msg.rows = 3;
      msg.placeholder = 'Goes into the email.';
      lab.appendChild(msg);
      body.appendChild(lab);
    }
    action(body, note ? 'Grant and email' : 'Save', async () => {
      close();
      await done(ymd, msg ? msg.value : '');
    }, 'primary');
    action(body, 'Cancel', () => close());
  });
}

/* ── users ───────────────────────────────────────────────────────────────── */

function personSheet(p, refresh) {
  sheet(p.email, (body, close) => {
    const meta = [];
    if (p.hasAccount === true) meta.push('Last seen ' + when(p.last_sign_in_at));
    else if (p.hasAccount === false) meta.push('No account yet');
    if (p.storage_bytes) meta.push(size(p.storage_bytes) + ' · ' + p.asset_count + ' files');
    if (p.hasAccount && !p.email_confirmed_at) meta.push('Email unconfirmed');
    if (meta.length) body.appendChild(el('p', 'msheet__note', meta.join(' · ')));

    const licensed = !!p.licence_active;
    action(body, licensed ? 'Change end date' : 'Grant Advanced', () => {
      close();
      pickWhen({
        title: (licensed ? 'Change' : 'Grant') + ' — ' + p.email,
        value: p.licence_expires_at ? toYMD(new Date(p.licence_expires_at)) : plusDays(30)
      }, async ymd => {
        await setLicence(p.email, endOfDay(ymd), p.note || '');
        toast(licensed ? 'Licence updated.' : 'Advanced granted.');
        refresh();
      });
    }, licensed ? null : 'primary');

    action(body, 'Send password reset', async () => {
      await sendPasswordReset(p.email);
      close();
      toast('Reset sent to ' + p.email + ', if that address has an account.');
    });

    action(body, 'Set a password', () => {
      close();
      sheet('Password for ' + p.email, (b2, c2) => {
        b2.appendChild(el('p', 'msheet__note',
          'Never emailed — read it out or send it however you normally would.'));
        const lab = el('label', 'mfield');
        lab.appendChild(el('span', null, 'New password (min 8)'));
        const inp = el('input', 'minput');
        inp.type = 'text';                 // shown: it has to be read out
        inp.autocapitalize = 'off'; inp.autocomplete = 'off'; inp.spellcheck = false;
        lab.appendChild(inp);
        b2.appendChild(lab);
        action(b2, 'Set password', async () => {
          await setUserPassword(p.email, inp.value);
          c2();
          toast('Password set.');
        }, 'primary');
        action(b2, 'Cancel', () => c2());
      });
    });

    action(body, 'History', () => {
      close();
      sheet('History — ' + p.email, async (b2) => {
        b2.appendChild(el('p', 'msheet__note', 'Loading…'));
        const rows = await licenceHistory(p.email);
        b2.textContent = '';
        if (!rows.length) {
          b2.appendChild(el('p', 'msheet__note',
            'Nothing recorded. Changes have been logged since 2 September 2026.'));
          return;
        }
        rows.forEach(e => {
          const r = el('div', 'mhist');
          r.appendChild(el('span', 'mhist__what', eventLine(e)));
          r.appendChild(el('span', 'mhist__when',
            formatDateTime(e.at) + (e.actor ? ' · ' + e.actor : '')));
          b2.appendChild(r);
        });
      });
    });

    if (licensed || p.licence_expires_at)
      action(body, 'Remove licence', () => {
        close();
        confirmSheet('Remove licence?', p.email + ' loses Motion and the watermark comes back.',
          'Remove licence', async () => { await removeLicence(p.email); toast('Licence removed.'); refresh(); });
      }, 'danger');

    if (p.hasAccount)
      action(body, 'Delete user', () => {
        close();
        confirmSheet('Delete this account?',
          p.email + ' and their projects are removed. The licence is keyed to the '
          + 'address and survives, so signing up again restores it.',
          'Delete user', async () => { await deleteUser(p.email); toast('Account deleted.'); refresh(); });
      }, 'danger');
  });
}

function eventLine(e) {
  const to = e.now_until ? formatDate(e.now_until) : null;
  const from = e.was_until ? formatDate(e.was_until) : null;
  switch (e.action) {
    case 'granted':        return to ? 'Granted, until ' + to : 'Granted, no expiry';
    case 'extended':       return 'Extended to ' + to + ', from ' + from;
    case 'shortened':      return 'Shortened to ' + to + ', from ' + from;
    case 'expiry_set':     return 'End date set to ' + to;
    case 'expiry_cleared': return 'Expiry removed — was ' + from;
    case 'note_changed':   return 'Note changed';
    case 'removed':        return from ? 'Removed, had run to ' + from : 'Removed';
    default:               return e.action;
  }
}

async function renderUsers(host) {
  host.textContent = '';
  host.appendChild(el('p', 'mempty', 'Loading…'));

  const [uRes, lRes, aRes, sRes, cRes] = await Promise.allSettled(
    [listUsers(), listLicences(), anonCount(), staleAnonCount(), cleanupLog(3)]);
  const users = uRes.status === 'fulfilled' ? uRes.value : null;
  const licences = lRes.status === 'fulfilled' ? lRes.value : [];
  const anon = aRes.status === 'fulfilled' ? aRes.value : 0;
  const stale = sRes.status === 'fulfilled' ? sRes.value : null;
  const runs = cRes.status === 'fulfilled' ? cRes.value : [];

  host.textContent = '';
  const refresh = () => renderUsers(host);
  const noteFor = {};
  licences.forEach(l => { noteFor[l.email.toLowerCase()] = l.note; });

  if (users === null) {
    host.appendChild(el('p', 'mempty', uRes.reason?.message || 'Couldn’t load accounts.'));
    return;
  }
  if (!users.length) host.appendChild(el('p', 'mempty', 'Nobody has signed up yet.'));

  users.forEach(u => {
    const p = {
      email: u.email, hasAccount: true,
      last_sign_in_at: u.last_sign_in_at, email_confirmed_at: u.email_confirmed_at,
      licence_expires_at: u.licence_expires_at, licence_active: u.licence_active,
      storage_bytes: u.storage_bytes, asset_count: u.asset_count,
      note: noteFor[u.email.toLowerCase()]
    };
    host.appendChild(personCard(p, refresh));
  });

  /* Licences with no account behind them yet — granted ahead of signup. */
  const known = new Set(users.map(u => u.email.toLowerCase()));
  const orphans = licences.filter(l => !known.has(l.email.toLowerCase()));
  if (orphans.length) {
    host.appendChild(el('h4', 'mgroup', 'Licensed, not signed up yet'));
    orphans.forEach(l => host.appendChild(personCard({
      email: l.email, hasAccount: false,
      licence_expires_at: l.expires_at,
      licence_active: !l.expires_at || new Date(l.expires_at) > new Date(),
      note: l.note
    }, refresh)));
  }

  const foot = el('div', 'mfoot');
  foot.appendChild(el('p', null, anon
    ? anon.toLocaleString() + ' anonymous sessions, not listed.'
    : 'No anonymous sessions.'));
  if (stale !== null) foot.appendChild(el('p', null, stale
    ? stale + ' are old and empty enough to delete on the next run.'
    : 'None are currently old enough and empty enough to delete.'));
  if (runs.length) {
    const r = runs[0];
    foot.appendChild(el('p', null, 'Cleanup last ran ' + when(r.ran_at)
      + (r.dry_run ? ' (dry run)' : ' — ' + r.deleted + ' deleted') + '.'));
  } else {
    foot.appendChild(el('p', null, 'No cleanup runs recorded yet.'));
  }
  host.appendChild(foot);

  /* WHICH ACCOUNT, said here rather than in the header. It was in the header
     on the reasoning that this screen changes other people's accounts and
     doing that as the wrong user is worth noticing — which is true, but it put
     an email address in the one piece of chrome on the screen, where it read
     as clutter and where iOS turned it into a mailto: link. Kept, moved: it
     sits with Sign out, which is the control it actually informs. */
  const me = currentUser()?.email;
  if (me) foot.appendChild(el('p', 'mfoot__me', 'Signed in as ' + me));

  /* Sign out lives at the foot of Users, not under the tab bar. It was below
     the nav, which pushed the tabs off the bottom edge and out of the thumb
     zone they exist to occupy — and this is the accounts tab, which is where
     somebody looking to change accounts would look. */
  const out = el('button', 'mout', 'Sign out');
  out.onclick = async () => { await signOut(); location.reload(); };
  host.appendChild(out);
}

function personCard(p, refresh) {
  const c = el('button', 'mcard');
  c.onclick = () => personSheet(p, refresh);
  const top = el('div', 'mcard__top');
  top.appendChild(el('span', 'mcard__title', p.email));
  const lapsed = p.licence_expires_at && new Date(p.licence_expires_at) <= new Date();
  const pill = el('span', 'mpill' + (p.licence_active ? ' mpill--on' : lapsed ? ' mpill--off' : ''));
  pill.textContent = p.licence_active
    ? (p.licence_expires_at ? formatDate(p.licence_expires_at) : 'No expiry')
    : lapsed ? 'Lapsed' : '—';
  top.appendChild(pill);
  c.appendChild(top);
  const sub = [];
  if (p.hasAccount === true) sub.push('Last seen ' + when(p.last_sign_in_at));
  else sub.push('No account yet');
  if (p.storage_bytes) sub.push(size(p.storage_bytes));
  if (p.note) sub.push(p.note);
  c.appendChild(el('span', 'mcard__sub', sub.join(' · ')));
  return c;
}

/* ── contact requests ────────────────────────────────────────────────────── */

async function renderTrials(host) {
  host.textContent = '';
  host.appendChild(el('p', 'mempty', 'Loading…'));
  const rows = await listTrials(200).catch(() => []);
  host.textContent = '';
  const refresh = () => { renderTrials(host); };

  const open = rows.filter(r => r.status === 'new').length;
  counts.trials = open;
  paintTabCounts();

  if (!rows.length) {
    host.appendChild(el('p', 'mempty',
      'Nothing yet. Messages arrive here from the contact form.'));
    return;
  }

  rows.forEach(t => {
    const isOpen = t.status === 'new';
    const c = el('button', 'mcard' + (isOpen ? ' mcard--new' : ''));
    const top = el('div', 'mcard__top');
    top.appendChild(el('span', 'mcard__title', t.company || t.email));
    const pill = el('span', 'mpill' + (t.licence_active ? ' mpill--on' : isOpen ? ' mpill--new' : ''));
    pill.textContent = t.licence_active ? 'Active' : isOpen ? 'New'
      : t.status === 'granted' ? 'Lapsed' : 'Dismissed';
    top.appendChild(pill);
    c.appendChild(top);
    c.appendChild(el('span', 'mcard__sub',
      [t.name, t.business_type, t.country, when(t.created_at)].filter(Boolean).join(' · ')));
    c.onclick = () => trialSheet(t, refresh);
    host.appendChild(c);
  });
}

function trialSheet(t, refresh) {
  sheet(t.company || t.email, (body, close) => {
    body.appendChild(el('p', 'msheet__note',
      [t.name, t.email].filter(Boolean).join(' · ')));
    body.appendChild(el('p', 'msheet__note',
      [t.business_type, t.country,
       t.source === 'licence' ? 'About a licence' : 'About credits',
       t.project_count ? t.project_count + ' projects' : null,
       'Asked ' + when(t.created_at)].filter(Boolean).join(' · ')));
    if (t.message) {
      const q = el('div', 'mquote');
      q.textContent = t.message;
      body.appendChild(q);
    }

    if (!t.licence_active)
      action(body, t.status === 'new' ? 'Approve' : 'Approve again', () => {
        close();
        pickWhen({ title: 'Grant a licence — ' + t.email, value: plusDays(30), note: true },
          async (ymd, note) => {
            const expiresAt = endOfDay(ymd);
            await setLicence(t.email, expiresAt, 'Contact · ' + t.company);
            await setTrialStatus(t.id, 'granted');
            try {
              await notifyLicence(t.email, expiresAt, note);
              toast('Licence granted — they have been emailed.');
            } catch (e) {
              toast('Licence granted, but the email did not send.', true);
            }
            refresh();
          });
      }, 'primary');

    const mail = el('a', 'mact', 'Reply by email');
    mail.href = 'mailto:' + t.email + '?subject='
      + encodeURIComponent('Your Illusio 3D request');
    body.appendChild(mail);

    if (t.status === 'new')
      action(body, 'Dismiss', async () => {
        await setTrialStatus(t.id, 'dismissed'); close(); toast('Dismissed.'); refresh();
      });

    action(body, 'Delete request', () => {
      close();
      confirmSheet('Delete this request?',
        'Removed entirely. Dismiss keeps the record instead.',
        'Delete', async () => { await deleteTrial(t.id); toast('Deleted.'); refresh(); });
    }, 'danger');
  });
}

/* ── feedback ────────────────────────────────────────────────────────────── */

async function renderFeedback(host) {
  host.textContent = '';
  host.appendChild(el('p', 'mempty', 'Loading…'));
  let rows = [];
  try { rows = await listFeedback({ limit: 100 }); }
  catch (e) { host.textContent = ''; host.appendChild(el('p', 'mempty', e.message)); return; }
  host.textContent = '';
  const refresh = () => { renderFeedback(host); };

  counts.feedback = rows.filter(r => !r.read_at).length;
  paintTabCounts();

  if (!rows.length) {
    host.appendChild(el('p', 'mempty',
      'Nothing yet. When someone uses the bug button, it turns up here.'));
    return;
  }

  rows.forEach(it => {
    const c = el('button', 'mcard' + (it.read_at ? '' : ' mcard--new'));
    const top = el('div', 'mcard__top');
    top.appendChild(el('span', 'mcard__title', it.reply_to || 'No reply address'));
    top.appendChild(el('span', 'mpill', when(it.created_at)));
    c.appendChild(top);
    c.appendChild(el('span', 'mcard__sub mcard__sub--clamp', it.message));
    c.onclick = () => feedbackSheet(it, refresh);
    host.appendChild(c);
  });
}

function feedbackSheet(it, refresh) {
  sheet(it.reply_to || 'Feedback', (body, close) => {
    body.appendChild(el('p', 'msheet__note', when(it.created_at)));
    const q = el('div', 'mquote');
    q.textContent = it.message;
    body.appendChild(q);

    const env = it.context?.env || {}, app = it.context?.app || {};
    const bits = [env.os, env.browser, env.gpu, env.viewport, app.stage, app.build]
      .filter(Boolean).join(' · ');
    if (bits) body.appendChild(el('p', 'msheet__note', bits));

    if (it.reply_to) {
      const mail = el('a', 'mact', 'Reply by email');
      mail.href = 'mailto:' + it.reply_to + '?subject='
        + encodeURIComponent('Re: your Illusio 3D feedback');
      body.appendChild(mail);
    }
    action(body, it.read_at ? 'Mark unread' : 'Mark read', async () => {
      await markRead([it.id], !it.read_at); close(); refresh();
    });
    action(body, 'Delete', () => {
      close();
      confirmSheet('Delete this message?',
        'Removed from the inbox and from the daily digest.',
        'Delete', async () => { await deleteFeedback(it.id); toast('Deleted.'); refresh(); });
    }, 'danger');
  });
}

/* ── shell ───────────────────────────────────────────────────────────────── */

/* Users first, matching the desktop panel's own order. Two screens onto the
   same three lists should not disagree about which comes first — whichever
   order is chosen, the cost of them differing is paid every time somebody
   moves between them. */
const TABS = [
  ['users',    'Users',    renderUsers],
  ['trials',   'Contact',  renderTrials],
  ['feedback', 'Feedback', renderFeedback]
];

function paintTabCounts() {
  TABS.forEach(([key]) => {
    const b = root?.querySelector('.mtab[data-k="' + key + '"] .mtab__count');
    if (!b) return;
    const n = counts[key] || 0;
    b.textContent = n ? String(n) : '';
    b.classList.toggle('hidden', !n);
  });
}

function show(key) {
  active = key;
  TABS.forEach(([k]) => {
    views[k].classList.toggle('hidden', k !== key);
    root.querySelector('.mtab[data-k="' + k + '"]')
      ?.setAttribute('aria-selected', String(k === key));
  });
}

const refreshActive = () => {
  const t = TABS.find(([k]) => k === active);
  if (t) t[2](views[active]);
};

export function mountAdminApp(host) {
  root = el('div', 'madmin');
  host.appendChild(root);

  const bar = el('header', 'mtop');
  /* The same logo files the topbar uses (ui/logo.js), so this reads as the
     product rather than as a tool that happens to touch its database — it is
     the only chrome on the screen and the only thing saying where you are. */
  const title = el('div', 'mtop__t');
  const h = el('span', 'mtop__h');
  h.appendChild(logoLockup());
  h.appendChild(el('span', 'mtop__sub', 'Admin'));
  title.appendChild(h);
  bar.appendChild(title);
  const rb = el('button', 'mtop__btn', 'Refresh');
  rb.onclick = () => refreshActive();
  bar.appendChild(rb);
  root.appendChild(bar);

  const main = el('main', 'mmain');
  TABS.forEach(([k]) => {
    views[k] = el('section', 'mview hidden');
    main.appendChild(views[k]);
  });
  root.appendChild(main);

  const nav = el('nav', 'mtabs');
  TABS.forEach(([k, label]) => {
    const b = el('button', 'mtab');
    b.dataset.k = k;
    b.setAttribute('role', 'tab');
    b.appendChild(el('span', 'mtab__l', label));
    b.appendChild(el('span', 'mtab__count hidden'));
    b.onclick = () => { show(k); if (!views[k].dataset.loaded) { views[k].dataset.loaded = '1'; TABS.find(t => t[0] === k)[2](views[k]); } };
    nav.appendChild(b);
  });
  root.appendChild(nav);

  /* Opens on Users, the first tab. The other two load on first visit rather
     than up front — three queries on open would make the slowest one the time
     to first paint. */
  show('users');
  views.users.dataset.loaded = '1';
  renderUsers(views.users);

  /* But both COUNTS are fetched immediately, even though their lists are not.
     A badge you only see after visiting the tab is not a badge, and these two
     are the whole reason to open this on a phone: somebody waiting on a trial,
     or a bug report nobody has read. */
  listTrials(200)
    .then(r => { counts.trials = r.filter(t => t.status === 'new').length; paintTabCounts(); })
    .catch(() => {});
  listFeedback({ unreadOnly: true, limit: 100 })
    .then(r => { counts.feedback = r.length; paintTabCounts(); })
    .catch(() => {});

}
